import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { InvoiceImportModel } from 'src/app/models/imports/invoice-import.model';
import { InvoiceModel } from 'src/app/models/invoicing/invoice.model';
import { InvoicesPrintModel } from 'src/app/models/invoicing/invoices-print.model';
import { InvoicesPrintsModel } from 'src/app/models/invoicing/invoices-prints.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {

  constructor(private _httpClient: HttpClient) { }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.ExportImportErrors}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  getAllAttachmentsWithPaginationAsync(invoiceId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('invoiceId', invoiceId).set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllAttachmentsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForAttachmentsAsync(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetColumnsForAttachmentsAsync}`);
  }

  getColumnsForInvoicesByClientAsync(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetColumnsForInvoicesByClientAsync}`);
  }

  getInvoicesByClientIdAsync(clientId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('clientId', clientId);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetInvoicesByClientIdAsync}`,
      filters, { params: params }
    );
  }

  getInvoiceById(invoiceId: number): Observable<InvoiceModel> {
    return this._httpClient.get<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetInvoiceById}/${invoiceId}`);
  }

  createInvoice(invoiceContract: InvoiceModel): Observable<InvoiceModel> {
    return this._httpClient.post<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.CreateInvoice}`,
      invoiceContract);
  }

  updateInvoice(invoiceContract: InvoiceModel): Observable<InvoiceModel> {
    return this._httpClient.put<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.UpdateInvoice}`,
      invoiceContract);
  }

  deleteInvoice(invoiceId: number): Observable<InvoiceModel> {
    return this._httpClient.delete<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.DeleteInvoice}/${invoiceId}`);
  }

  setStateInvoice(invoiceId: number): Observable<InvoiceModel> {
    return this._httpClient.put<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.SetInvoiceState}/${invoiceId}`,
      null);
  }

  blockInvoice(invoiceId: number): Observable<InvoiceModel> {
    return this._httpClient.put<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.BlockInvoice}/${invoiceId}`, null);
  }

  setInvoiceWalletType(invoiceId: number): Observable<InvoiceModel> {
    return this._httpClient.put<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.SetInvoiceWalletType}/${invoiceId}`,
      null);
  }

  getPermissionPrint(): Observable<boolean> {
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetPermissionPrint}`);
  }

  printInvoices(itemFilters: Array<ItemFilterModel>): Observable<Array<InvoicesPrintModel>> {
    return this._httpClient.post<Array<InvoicesPrintModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.PrintInvoices}`, itemFilters);
  }

  printsInvoices(invoiceId: number, consecutiveInitial: number | null, consecutiveFinal: number | null, print: boolean): Observable<Array<InvoicesPrintsModel>> {
    let params = new HttpParams()
    params = params.set('invoiceId', invoiceId.toString());
    params = params.set('print', print);
    if (consecutiveInitial !== null)
      params = params.set('consecutiveInitial', consecutiveInitial.toString());
    if (consecutiveFinal !== null)
      params = params.set('consecutiveFinal', consecutiveFinal.toString());
    return this._httpClient.get<Array<InvoicesPrintsModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.PrintsInvoices}`,
      { params: params }
    );
  }

  getPrintInvoiceForFormat(invoiceId: number): Observable<InvoicesPrintModel> {
    return this._httpClient.get<InvoicesPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetPrintInvoiceForFormat}/${invoiceId}`);
  }

  getInvoiceByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, consecutive: number): Observable<InvoiceModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    params = params.set('bussinessDocumentsTypeId', bussinessDocumentsTypeId);
    params = params.set('consecutive', consecutive);
    return this._httpClient.get<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetInvoiceByKeys}`, { params: params });
  }

  getInvoiceByParamsAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, consecutive: number): Observable<InvoiceModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    params = params.set('bussinessDocumentsTypeId', bussinessDocumentsTypeId);
    params = params.set('consecutive', consecutive);

    return this._httpClient.get<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetInvoiceByParams}`, { params: params });
  }

  getFirstInvoiceByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number): Observable<InvoiceModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    params = params.set('bussinessDocumentsTypeId', bussinessDocumentsTypeId);
    return this._httpClient.get<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetFirstInvoiceByKeys}`, { params: params });
  }

  getLastInvoiceByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number): Observable<InvoiceModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    params = params.set('bussinessDocumentsTypeId', bussinessDocumentsTypeId);
    return this._httpClient.get<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetLastInvoiceByKeys}`, { params: params });
  }

  getPreviusInvoiceByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, consecutive: number): Observable<InvoiceModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    params = params.set('bussinessDocumentsTypeId', bussinessDocumentsTypeId);
    params = params.set('consecutive', consecutive);
    return this._httpClient.get<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetPreviusInvoiceByKeys}`, { params: params });
  }

  getNextInvoiceByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, consecutive: number): Observable<InvoiceModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    params = params.set('bussinessDocumentsTypeId', bussinessDocumentsTypeId);
    params = params.set('consecutive', consecutive);
    return this._httpClient.get<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetNextInvoiceByKeys}`, { params: params });
  }

  copyInvoice(idInvoiceDestinity: number, bussinessDocumentsTypeId: number, consecutive: number): Observable<InvoiceModel> {
    return this._httpClient.post<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.CopyInvoice}/${idInvoiceDestinity}/${bussinessDocumentsTypeId}/${consecutive}`, null);
  }

  verifyInvoiceContado(invoiceId: number): Observable<boolean> {
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.VerifyInvoiceContado}/${invoiceId}`);
  }

  attachFilesToInvoice(idInvoice: number, files: FormData): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.AttachFilesToInvoice}/${idInvoice}`, files);
  }

  getFilesToInvoice(invoiceId: number): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetFilesToInvoice}/${invoiceId}`);
  }

  downloadFileToInvoice(invoiceId: number, file: string): Observable<HttpEvent<Blob>> {
    const itemFilterResponse = new ItemFilterModel("file", null, invoiceId, file);
    return this._httpClient.request(new HttpRequest('POST', `${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.DownloadFileToInvoice}`,
      itemFilterResponse, { reportProgress: true, responseType: 'blob' }));
  }

  deleteFilesToInvoice(invoiceId: number, file: string): Observable<Array<string>> {
    const itemFilterResponse = new ItemFilterModel("file", null, invoiceId, file);
    return this._httpClient.post<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.DeleteFilesToInvoice}`,
      itemFilterResponse);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  importInvoice(invoicesImport: Array<InvoiceImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.ImportInvoices}`,
      invoicesImport);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetColumnsForGeneralConsultation}`);
  }

  getInvoicesWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllInvoicesWithPagination}`,
      filters, { params: params }
    );
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams()
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getAccountingInterfaceCode(accountingInterfaceId: number): Observable<number> {
    return this._httpClient.get<{ value: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAccountingInterfaceCodeAsync}/${accountingInterfaceId}`)
      .pipe(map(response => response.value));
  }

  getAllInvoiceOperationsForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllInvoiceOperationsForSelectAsync}`,);
  }

  getAllClientsForSelectAsync(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllClientsForSelectAsync}`,);
  }

  getAllCostCentersForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllCostCenterForSelectAsync}`,);
  }

  getAllCostCenterByBranchIdForSelect(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllCostCenterByBranchIdForSelectAsync}`,
      { params: params }
    );
  }

  getAllCostCenterForPreinvoiceSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllCostCenterForPreinvoiceSelectAsync}`,);
  }

  getAllTypeDIANForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllTypeDIANForSelectAsync}`,);
  }

  getAllPaymentMethodsForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllPaymentMethodsForSelectAsync}`,);
  }

  getAllInvoiceWalletTypeForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllInvoiceWalletTypeForSelectAsync}`,);
  }

  getAllIvaTypeForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllIvaTypeForSelectAsync}`,);
  }

  getRentalPeriodByIdForRegisterInvoiceAsync(rentalPeriodId: number): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetRentalPeriodByIdForRegisterInvoiceAsync}/${rentalPeriodId}`,);
  }

  getClientByIdForRegisterInvoiceAsync(clientId: number): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetClientByIdForRegisterInvoiceAsync}/${clientId}`,);
  }

  getElectronicInvoiceInterfaceCodeById(electronicInvoiceInterfaceId: number): Observable<number> {
    return this._httpClient.get<{ value: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetElectronicInvoiceInterfaceCodeByIdAsync}/${electronicInvoiceInterfaceId}`)
      .pipe(map(response => response.value));
  }

  getInvoicingCutoffCodeByIdAsync(invoicingCutoffId: number): Observable<number> {
    return this._httpClient.get<{ value: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetInvoicingCutoffCodeByIdAsync}/${invoicingCutoffId}`)
      .pipe(map(response => response.value));
  }

  getAllBussinessDocumentsTypesForSelectAsync(branchId: number, enterpriseId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId).set('enterpriseId', enterpriseId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllBussinessDocumentsTypesForSelectAsync}`,
      { params }
    );
  }

  getAllZonesForSelectsAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllZonesForSelectsAsync}`,);
  }

  getAllZonesByBranchIdForSelects(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetAllZonesByBranchIdForSelectsAsync}`,
      { params: params }
    );
  }

  getInvoiceCodeByIdForPrintingRange(invoiceId: number): Observable<number> {
    return this._httpClient.get<{ value: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetInvoiceCodeByIdForPrintingRangeAsync}/${invoiceId}`)
      .pipe(map(response => response.value));
  }

  getMovementCodeById(movementId: number): Observable<number> {
    return this._httpClient.get<{ value: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GetMovementCodeByIdAsync}/${movementId}`)
      .pipe(map(response => response.value));
  }

  /********* GENERACION DE PDF *********** */
  generateInvoiceToPdf(invoiceIds: number[]): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoice.GenerateInvoiceToPdf}`, invoiceIds);
  }
}
